import {
  Button,
  Chip,
  Dialog,
  IconButton,
  Select,
  Switch,
  Text,
} from '@/components/DesignSystem';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { ConfirmationDialog } from '@/constants/constant';
import { useDeleteMember, useUpdateMember } from '@/hooks/useRetailer';
import { ApiMember } from '@api/interfaces';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import { Divider, MenuItem, Stack } from '@mui/material';
import { formatPhoneNumber } from '@shared/utilities';
import React, { useState } from 'react';
import AddMember from './MemberFormModal';
import { hasUserPermission, UserPermission } from '@shared/enums';

interface MemberProfileCardProps {
  isLocation?: boolean;
  length?: number;
  member: ApiMember;
}
const MemberProfileCard = ({ isLocation, member }: MemberProfileCardProps) => {

  const [showEditMember, setShowEditMember] = useState(false);
  const [showDeleteMember, setShowDeleteMember] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showChangeIsActive, setShowChangeIsActive] = useState(false);
  const [error, setError] = useState('');

  const onClose = () => {
    setShowConfirmModal(false);
    setShowDeleteMember(false);
  };

  const { updateMember: updateSalesPerson, isLoading: isUpdateLoading } =
    useUpdateMember(member?.id ?? '', isLocation, onClose);

  const { deleteMember, isLoading } = useDeleteMember(member?.id ?? '', onClose, setError);

  const { updateMember: updateMemberIsActive, isLoading: isSetActiveLoading } =
    useUpdateMember(member?.id ?? '', isLocation, () => setShowChangeIsActive(false));

  const onSwitch = () => {
    !member?.defaultSalesperson ? setShowConfirmModal(true) : () => null;
  };

  const hasNotificationBuilderPermission = hasUserPermission(
    member?.permissions,
    UserPermission.CanAccessNotificationsBuilder,
  );

  const hasReportsPermission = hasUserPermission(
    member?.permissions,
    UserPermission.CanAccessReports,
  );

  return (
    <>
      <Divider />
      <Stack
        sx={{
          padding: '20px',
        }}
      >
        <Stack>
          <Stack direction='row' justifyContent={'space-between'}>
          <Stack direction='column'>
          <Stack direction="row" gap="16px">
            <Text category='h6' sx={{ flex: 1, textTransform: 'capitalize' }}>
              {member?.businessName ?? ''}
            </Text>
            <Stack alignItems="flex-end" direction="column" gap="8px">
              {member?.isTest && (
                <Chip color="primary" label="Test User" testID="test-user-chip" />
              )}
              {hasNotificationBuilderPermission && (
                <Chip
                  color="info"
                  label="Notification Builder"
                  testID="notification-builder-chip"
                />
              )}
              {hasReportsPermission && (
                <Chip
                  color="warning"
                  label="Reports"
                  testID="reports-chip"
                />
              )}
            </Stack>
          </Stack>
          <Text mb='10px' mt='5px'>
            +1 {formatPhoneNumber(member.telephone ?? '', 'paren')} •{' '}
            {member?.email ?? ''}
          </Text>
          </Stack>
          </Stack>
          <Stack
            alignItems='center'
            direction='row'
            justifyContent={'space-between'}
          >
            <Select
              disabled
              label='Role'
              testID='role'
              value={isLocation ? 'sales-person' : 'role-admin'}
            >
              <MenuItem value='role-admin'>Retailer Admin</MenuItem>
              <MenuItem value='sales-person'>Salesperson</MenuItem>
            </Select>
            <Stack alignItems='center' direction='row'>
              <Stack alignItems='flex-end' direction={isLocation ? 'column' : 'row'}>
                {isLocation && (
                  <Switch
                    checked={member?.defaultSalesperson ?? false}
                    label='Default'
                    labelPlacement='start'
                    onClick={onSwitch}
                    sx={{ marginRight: '5px' }}
                    testID='default-salesperson-toggle'
                  />
                )}
                <Switch
                  checked={member?.isActive ?? false}
                  label={member?.isActive ? 'Active' : 'Inactive'}
                  labelPlacement='start'
                  onClick={() => setShowChangeIsActive(true)}
                  sx={{ marginRight: '5px' }}
                  testID='active-toggle'
                />
              </Stack>
              <IconButton
                onClick={() => setShowEditMember(true)}
                testID='edit-member'
              >
                <EditOutlined />
              </IconButton>
              {!isLocation && (
                <IconButton
                  onClick={() => setShowDeleteMember(true)}
                  testID='delete-member'
                >
                  <DeleteOutlined />
                </IconButton>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      {showEditMember && (
        <AddMember
          isEdit={true}
          isLocation={isLocation}
          member={{
            ...member,
            businessName: member?.businessName ?? '',
            email: member?.email ?? '',
            permissions: member?.permissions ?? {},
            telephone: member?.telephone ?? '',
            role: isLocation ? 'sales-person' : 'retailer-admin',
          }}
          onClose={() => setShowEditMember(false)}
          show={showEditMember}
        />
      )}
      {showDeleteMember && (
        <ConfirmDialog
          isLoading={isLoading}
          onCancel={() => setShowDeleteMember(false)}
          onConfirm={() => {
            deleteMember();
            setShowDeleteMember(false);
          }}
          open={showDeleteMember}
          testID="delete-member-confirm-dialog"
          title={ConfirmationDialog.deleteMemberTitle}
        >
          {ConfirmationDialog.deleteMemberMessage}
        </ConfirmDialog>
      )}
      {showConfirmModal && (
        <ConfirmDialog
          isLoading={isUpdateLoading}
          onCancel={() => setShowConfirmModal(false)}
          onConfirm={() => {
            updateSalesPerson({
              defaultSalesperson: true,
            });
            setShowConfirmModal(false);
          }}
          open={showConfirmModal}
          testID="change-default-salesperson-confirm-dialog"
          title={ConfirmationDialog.changeDefaultSalesPersonTitle}
        >
          {ConfirmationDialog.changeDefaultSalesPersonMessage}
        </ConfirmDialog>
      )}
      {showChangeIsActive && (
        <ConfirmDialog
          isLoading={isSetActiveLoading}
          onCancel={() => setShowChangeIsActive(false)}
          onConfirm={() => updateMemberIsActive({ isActive: !member?.isActive })}
          open={showChangeIsActive}
          testID="change-member-active-confirm-dialog"
          title={
            member?.isActive
              ? ConfirmationDialog.deactivateMemberTitle
              : ConfirmationDialog.activateMemberTitle
          }
        >
          {member?.isActive
            ? ConfirmationDialog.deactivateMemberMessage
            : ConfirmationDialog.activateMemberMessage}
        </ConfirmDialog>
      )}
      {!!error && (
        <Dialog
          acceptButton={(props) => (
            <Button
              {...props}
              onClick={() => {
                onClose();
                setError('');
              }}
              variant="contained"
            >
              Close
            </Button>
          )}
          onClose={() => {
            onClose();
            setError('');
          }}
          open={!!error}
          showCloseButton={false}
          testID="wallet-description-dialog"
        >
          {error}
        </Dialog>
      )}
    </>
  );
};

export default MemberProfileCard;
